import { Check, X } from "lucide-react";
import { useMemo, useState, type FC, type ReactNode } from "react";

import type { OutgoingIntakeQuestionType } from "@/api/types/outgoingIntake";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

import { compatibleTargets, useSaveTargets } from "../saveTargets";

type Props = {
  value: string | null;
  questionType?: OutgoingIntakeQuestionType | null;
  onPick: (modelPath: string | null) => void;
  children: ReactNode;
  align?: "start" | "end";
};

/** Pick the profile field an answer is written to when the intake is accepted. */
export const SaveToPicker: FC<Props> = ({
  value,
  questionType,
  onPick,
  children,
  align = "start"
}) => {
  const [open, setOpen] = useState(false);
  const targets = useSaveTargets();
  const groups = useMemo(() => {
    const out = new Map<string, typeof targets>();
    for (const t of compatibleTargets(targets, questionType)) {
      const list = out.get(t.group) ?? [];
      list.push(t);
      out.set(t.group, list);
    }
    return Array.from(out.entries());
  }, [questionType, targets]);

  const pick = (path: string | null) => {
    onPick(path);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search fields…" className="text-xs" />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
              {targets.length ? "No matching field." : "Loading fields…"}
            </CommandEmpty>
            {groups.map(([group, list]) => (
              <CommandGroup key={group} heading={group}>
                {list.map((t) => (
                  <CommandItem
                    key={t.model_path}
                    value={`${t.label} ${t.model_path}`}
                    onSelect={() => pick(t.model_path)}
                    className="text-xs"
                  >
                    <span className="min-w-0 flex-1 truncate">{t.label}</span>
                    {t.model_path === value ? (
                      <Check className="size-3.5 text-primary" />
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            {value ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__none__ don't save"
                    onSelect={() => pick(null)}
                    className="text-xs text-muted-foreground"
                  >
                    <X className="size-3.5" /> Don't save this answer
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
